import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import {
  CompanyImport,
  CompanyImportResponse,
  JobStatus,
} from '../../../../../../../../../core/model/company-import-text.model';
import { CompanyType, Source } from '../../../../../../../../../core/model/company.model';

@Component({
  selector: 'app-massive-company-validation',
  templateUrl: './massive-company-validation.component.html',
  styleUrls: ['./massive-company-validation.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MassiveCompanyValidationComponent implements OnInit, OnChanges, OnDestroy {

  @Input() jobStatus!: CompanyImportResponse;
  @Output() formReady = new EventEmitter<UntypedFormGroup>();

  form: UntypedFormGroup;
  sources = Object.values(Source);
  JobStatus = JobStatus;

  isMainCompanySeller = false;

  private _unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private _formBuilder: UntypedFormBuilder,
  ) {
  }

  ngOnInit(): void {
    this.isMainCompanySeller = this.getMainCompany().companyType === CompanyType.seller;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobStatus.firstChange && changes.jobStatus && changes.jobStatus.currentValue.status === JobStatus.COMPLETED) {
      this.form = this._formBuilder.group({
        jobId: new UntypedFormControl(this.jobStatus.id),
        mainCompany: this.buildCompanyForm(this.getMainCompany(), true),
        relatedCompanies: new UntypedFormArray([]),
      });

      this.getRelatedCompanies().forEach((company: CompanyImport) => {
        this.relatedFormsGroups().push(this.buildCompanyForm(company, false));
      });

      this.formReady.emit(this.form);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  relatedFormsGroups(): UntypedFormArray {
    return (this.form.get('relatedCompanies') as UntypedFormArray);
  }

  getMainCompanyDescription(): string {
    return this.getCompanyDescription(this.getMainCompany());
  }

  getRelatedCompanyDescription(index: number): string {
    return this.getCompanyDescription(this.getRelatedCompanies()[index]);
  }

  getMainCompanyType(): CompanyType {
    return this.getMainCompany().companyType;
  }

  private getCompanyDescription(company: CompanyImport): string {
    return `${company.businessName} (${company.taxCodePrefix} ${company.taxCode})`;
  }

  private getMainCompany(): CompanyImport {
    return this.jobStatus.output.mainCompany;
  }

  private getRelatedCompanies(): CompanyImport[] {
    return this.jobStatus.output.relatedCompanies;
  }

  private buildCompanyForm(company: CompanyImport, isMainCompany: boolean): UntypedFormGroup {
    const form: UntypedFormGroup = this._formBuilder.group({
      taxCodePrefix: new UntypedFormControl(company.taxCodePrefix),
      taxCode: new UntypedFormControl(company.taxCode),
      legalRepresentativeFirstName: new UntypedFormControl(company.legalRepresentativeFirstName || null, isMainCompany ? null : Validators.required),
      legalRepresentativeLastName: new UntypedFormControl(company.legalRepresentativeLastName || null, Validators.required),
      legalRepresentativeEmail: new UntypedFormControl(company.legalRepresentativeEmail || null, [Validators.required, Validators.email]),
      legalRepresentativePhone: new UntypedFormControl(company.legalRepresentativePhone || null, isMainCompany ? null : Validators.required),
    });

    if (company.companyType === CompanyType.seller || isMainCompany) {
      form.addControl('source', new UntypedFormControl(company.source));
      form.addControl('partnerId', new UntypedFormControl(company.partnerId));
      form.addControl('referrerId', new UntypedFormControl(company.referrerId));
    }

    return form;
  }

}
